import { useRef, useState } from 'react';
import {
  useVisitaAllegati,
  useUploadVisitaAllegato,
  useDeleteVisitaAllegato,
} from '@offertify/query';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Camera,
  Download,
  ExternalLink,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { visiteAPI } from '../../utils/api';
import { usePermissions } from '../../hooks/usePermissions';
import ConfirmModal from '../ConfirmModal';
import type { VisitaAllegato } from '@offertify/core';

// Mirror of backend/src/core/visita_allegati_constants.py — the backend stays the
// source of truth, these only give instant feedback before uploading.
const ALLOWED_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'webp', 'heic', 'heif', 'csv', 'txt',
];
const MAX_ATTACHMENTS = 30;
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const ACCEPT = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');
// Formats every browser can render in an <img>; HEIC/HEIF only in Safari, so
// they fall back to the file icon (the onError handler covers Safari too).
const PREVIEWABLE_IMAGES = ['image/jpeg', 'image/png', 'image/webp'];

type UploadItem = {
  key: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'error';
  error?: string;
};

function extensionOf(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(allegato: Pick<VisitaAllegato, 'content_type'>) {
  const ct = allegato.content_type;
  if (ct.startsWith('image/')) return ImageIcon;
  if (ct.includes('spreadsheet') || ct.includes('excel') || ct === 'text/csv') return FileSpreadsheet;
  if (ct === 'application/pdf' || ct.includes('word') || ct === 'text/plain') return FileText;
  return FileIcon;
}

function errorMessage(error: any): string {
  if (!error?.response) return 'Connessione assente o interrotta: riprova.';
  const detail = error.response.data?.detail;
  if (typeof detail === 'string') return detail;
  if (error.response.status === 403) return 'Non hai i permessi per questa operazione.';
  if (error.response.status === 413) return 'File troppo grande.';
  return 'Caricamento non riuscito.';
}

/** Pre-validation client-side; returns an error message or null. */
function validate(file: File): string | null {
  if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
    return 'Formato non supportato.';
  }
  if (file.size === 0) return 'Il file è vuoto.';
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File troppo grande (max ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB).`;
  }
  return null;
}

interface AttivitaAllegatiProps {
  visitaId: string;
  /** Titolo nascosto quando il componente è già dentro un pannello con intestazione. */
  compact?: boolean;
}

/**
 * Foto e documenti di un'attività (issue #105): anteprima, upload multiplo con
 * progresso ed errori per file, download ed eliminazione (soft delete).
 * Permessi allineati all'attività: visite:view per vedere, visite:edit per
 * caricare/eliminare; il backend applica anche il cono di visibilità.
 */
export default function AttivitaAllegati({ visitaId, compact = false }: AttivitaAllegatiProps) {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('visite:view');
  const canEdit = hasPermission('visite:edit');

  const { data: allegati = [], isLoading, isError, refetch } = useVisitaAllegati(
    canView ? visitaId : undefined,
  );
  const uploadMutation = useUploadVisitaAllegato(visitaId);
  const deleteMutation = useDeleteVisitaAllegato(visitaId);

  const [queue, setQueue] = useState<UploadItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState<VisitaAllegato | null>(null);
  const [toDelete, setToDelete] = useState<VisitaAllegato | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [brokenPreviews, setBrokenPreviews] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const isUploading = queue.some((q) => q.status === 'uploading' || q.status === 'pending');

  const patchItem = (key: string, patch: Partial<UploadItem>) =>
    setQueue((prev) => prev.map((q) => (q.key === key ? { ...q, ...patch } : q)));

  const uploadOne = async (item: UploadItem) => {
    patchItem(item.key, { status: 'uploading', progress: 0, error: undefined });
    try {
      await uploadMutation.mutateAsync({
        file: item.file,
        onProgress: (progress) => patchItem(item.key, { progress }),
      });
      setQueue((prev) => prev.filter((q) => q.key !== item.key));
      return true;
    } catch (error) {
      patchItem(item.key, { status: 'error', error: errorMessage(error) });
      return false;
    }
  };

  const enqueue = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;

    const room = MAX_ATTACHMENTS - allegati.length - queue.length;
    const items: UploadItem[] = list.map((file, i) => {
      const invalid = validate(file) ?? (i >= room ? `Limite di ${MAX_ATTACHMENTS} allegati raggiunto.` : null);
      return {
        key: `${Date.now()}-${i}-${file.name}`,
        file,
        progress: 0,
        status: invalid ? 'error' : 'pending',
        error: invalid ?? undefined,
      };
    });
    setQueue((prev) => [...prev, ...items]);

    // Sequential: keeps memory/bandwidth bounded on mobile connections and
    // makes the per-file progress readable.
    let ok = 0;
    for (const item of items.filter((i) => i.status === 'pending')) {
      if (await uploadOne(item)) ok += 1;
    }
    if (ok > 0) toast.success(ok === 1 ? 'Allegato caricato' : `${ok} allegati caricati`);
  };

  const handleDownload = async (allegato: VisitaAllegato) => {
    setDownloadingId(allegato.id);
    try {
      const res = await visiteAPI.getAllegatoDownloadUrl(visitaId, allegato.id);
      // Content-Disposition: attachment → the browser downloads without leaving the page.
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDownloadingId(null);
    }
  };

  const openPreview = (allegato: VisitaAllegato) => {
    if (PREVIEWABLE_IMAGES.includes(allegato.content_type) && !brokenPreviews.has(allegato.id)) {
      setLightbox(allegato);
    } else if (allegato.preview_url) {
      window.open(allegato.preview_url, '_blank', 'noopener,noreferrer');
    } else {
      handleDownload(allegato);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteMutation.mutateAsync(toDelete.id);
      toast.success('Allegato eliminato');
      if (lightbox?.id === toDelete.id) setLightbox(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setToDelete(null);
    }
  };

  if (!canView) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {!compact && (
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">Foto e documenti</span>
            <span className="text-xs text-gray-400">
              {allegati.length}/{MAX_ATTACHMENTS}
            </span>
          </div>
        )}
        {canEdit && allegati.length < MAX_ATTACHMENTS && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:hidden"
            >
              <Camera className="h-3.5 w-3.5" />
              Scatta foto
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Upload className="h-3.5 w-3.5" />
              Allega file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                if (e.target.files) enqueue(e.target.files);
                e.target.value = '';
              }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) enqueue(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
        )}
      </div>

      {/* Drop zone + grid */}
      <div
        onDragOver={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          setDragOver(false);
          enqueue(e.dataTransfer.files);
        }}
        className={`rounded-lg transition-colors ${dragOver ? 'ring-2 ring-blue-400 bg-blue-50/50' : ''}`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Caricamento allegati...
          </div>
        ) : isError ? (
          <div className="flex items-center gap-2 py-3 text-xs text-red-600">
            <AlertCircle className="h-4 w-4" />
            Impossibile caricare gli allegati.
            <button type="button" onClick={() => refetch()} className="underline">
              Riprova
            </button>
          </div>
        ) : allegati.length === 0 && queue.length === 0 ? (
          <div className="py-4 text-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
            {canEdit ? 'Nessun allegato. Trascina qui foto o documenti.' : 'Nessun allegato.'}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {allegati.map((allegato) => {
              const Icon = iconFor(allegato);
              const showThumb =
                PREVIEWABLE_IMAGES.includes(allegato.content_type) &&
                !!allegato.preview_url &&
                !brokenPreviews.has(allegato.id);
              return (
                <div
                  key={allegato.id}
                  className="group relative aspect-square rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => openPreview(allegato)}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-2"
                    title={allegato.descrizione || allegato.filename}
                  >
                    {showThumb ? (
                      <img
                        src={allegato.preview_url!}
                        alt={allegato.filename}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                        onError={() => setBrokenPreviews((prev) => new Set(prev).add(allegato.id))}
                      />
                    ) : (
                      <>
                        <Icon className="h-7 w-7 text-gray-400" />
                        <span className="w-full truncate text-center text-[11px] text-gray-600">
                          {allegato.filename}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {formatSize(allegato.file_size_bytes)}
                        </span>
                      </>
                    )}
                  </button>
                  <div className="absolute top-1 right-1 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => handleDownload(allegato)}
                      disabled={downloadingId === allegato.id}
                      className="p-1 rounded bg-white/90 text-gray-700 shadow hover:bg-white"
                      title="Scarica"
                    >
                      {downloadingId === allegato.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => setToDelete(allegato)}
                        className="p-1 rounded bg-white/90 text-red-600 shadow hover:bg-white"
                        title="Elimina"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload queue: progress + per-file errors with retry */}
      {queue.length > 0 && (
        <ul className="space-y-1.5">
          {queue.map((item) => (
            <li
              key={item.key}
              className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${
                item.status === 'error' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
              }`}
            >
              {item.status === 'error' ? (
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 text-red-600" />
              ) : (
                <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-blue-600" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-gray-800">{item.file.name}</div>
                {item.status === 'error' ? (
                  <div className="text-red-700">{item.error}</div>
                ) : (
                  <div className="mt-1 h-1 rounded bg-gray-100">
                    <div
                      className="h-1 rounded bg-blue-600 transition-all"
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                )}
              </div>
              {item.status === 'error' && (
                <>
                  {validate(item.file) === null && (
                    <button
                      type="button"
                      onClick={() => uploadOne(item)}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Riprova"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setQueue((prev) => prev.filter((q) => q.key !== item.key))}
                    className="p-1 text-gray-400 hover:text-gray-700"
                    title="Rimuovi"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Lightbox for image previews */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-h-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.preview_url!}
              alt={lightbox.filename}
              className="max-h-[80vh] max-w-full rounded-lg object-contain"
            />
            <div className="mt-2 flex items-center justify-between gap-3 text-sm text-white">
              <div className="min-w-0">
                <div className="truncate">{lightbox.filename}</div>
                <div className="text-xs text-white/70">
                  {formatSize(lightbox.file_size_bytes)}
                  {lightbox.created_by_name && ` · ${lightbox.created_by_name}`}
                  {` · ${new Date(lightbox.created_at).toLocaleDateString('it-IT')}`}
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href={lightbox.preview_url!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded bg-white/10 hover:bg-white/20"
                  title="Apri in una nuova scheda"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                <button
                  type="button"
                  onClick={() => handleDownload(lightbox)}
                  className="p-2 rounded bg-white/10 hover:bg-white/20"
                  title="Scarica"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setLightbox(null)}
                  className="p-2 rounded bg-white/10 hover:bg-white/20"
                  title="Chiudi"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Elimina allegato"
        message={`Eliminare "${toDelete?.filename}" dall'attività?`}
        confirmLabel="Elimina"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
